import AppBar from '@material-ui/core/AppBar'
import PropTypes from 'prop-types'
import React, { Suspense, useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Col, Row, Button, Tooltip } from 'reactstrap'
import {
  clearPaginatedProducts,
  deleteSession,
  fetchLocations,
  dispatchSweetAlert,
  fetchLocationProducts,
  setLoading
} from '../../actions'
import { fetchCurrentStoreView } from '../../redux/storeConfig/slices/slice.storeViews/storeViews.thunks'
import { isPunchoutOnlyLocation, isPunchoutUiMode } from '../../util/punchout'
import { legacyLogOut, enterpriseLogOut } from '../../util/utils'
import { withJamParams } from '../../util/recordingNavigation'
import AdminHeaderActions from '../AdminUser/AdminHeaderActions'
import LoadingSpinner from '../general/LoadingSpinner'
import SelectPaginate from '../general/SelectPaginate'
import { switchStoreViewAndRefreshProducts } from '../Shop/storeViewProductRefresh'
import MyAccountDropdown from '../widgets/MyAccountDropdown'
import './headerDropdown.css'
import LegacyHeaderCart from './Cart'
import CreditDisplayBox from './CreditDisplayBox'
import { getHeaderColorOverride } from './headerColor'
import HeaderMenu from './HeaderMenu'
import SearchBar from './SearchBar'

// Direct component imports intentional: React.lazy() requires concrete module paths.
// Importing from the barrel (src/v2/header/index.ts) would defeat code-splitting.
const HeaderCart = React.lazy(() => import('../../v2/header/components/HeaderCart'))
const CreditDisplay = React.lazy(() => import('../../v2/header/components/CreditDisplay'))
const getSelectableLocations = locations => {
  const availableLocations = locations ?? []
  return availableLocations.filter(location => !isPunchoutOnlyLocation(location))
}

const HeaderOne = props => {
  const [showLinks] = useState(false)
  const [fixedHeader, setFixedHeader] = useState(false)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const history = useHistory()
  const location = useLocation()
  const [clientNameTooltipOpen, setClientNameTooltipOpen] = useState(false)

  const isV2 = props.checkoutFlag.status === 'ready' && props.checkoutFlag.enabled
  const { userCredits, portal, customerGroup, currentLocation, currentUser, isAdminUserPath } = props
  const hasPunchoutSession = !!props.session?.punchout_buyer
  const isPunchoutMode = isPunchoutUiMode({ session: props.session, currentLocation })
  const canSwitchLocations = !hasPunchoutSession
  const selectableLocations = getSelectableLocations(props.locations)

  const calculateNoCredits = userCredits => {
    const { budget, coop_credit, credit_note, gift_card_credit, lms_points } = userCredits || {}
    return [budget, coop_credit, credit_note, gift_card_credit, lms_points].every(credit => {
      return credit && credit.remaining === 0
    })
  }

  const [noCredits, setNoCredits] = useState(calculateNoCredits(userCredits))

  const updateWindowWidth = () => {
    setWindowWidth(window.innerWidth)
  }

  const hideBar = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
    if (scrollTop >= 200) {
      setFixedHeader(true)
    } else {
      setFixedHeader(false)
    }
  }

  useEffect(() => {
    updateWindowWidth()
    window.addEventListener('resize', updateWindowWidth)
    window.addEventListener('scroll', hideBar)
    return () => {
      window.removeEventListener('scroll', hideBar)
      window.removeEventListener('resize', updateWindowWidth)
    }
  }, [currentLocation?.id, currentUser.id])

  useEffect(() => {
    checkCredits()
  }, [userCredits])

  const changeLocation = async selectedLocation => {
    const result = await switchStoreViewAndRefreshProducts({
      selectedStoreViewId: selectedLocation.id,
      pathname: location.pathname,
      search: location.search,
      fetchCurrentStoreView: props.fetchCurrentStoreView,
      fetchLocationProducts: props.fetchLocationProducts,
      setLoading: props.setLoading,
      clearPaginatedProducts: props.clearPaginatedProducts
    })

    if (result.shouldClearSearch) {
      history.replace({ pathname: location.pathname, search: result.nextSearch })
    }

    setDropdownOpen(false)
  }
  const signOut = () => {
    legacyLogOut()
    const isEnterpriseLogout = enterpriseLogOut()

    if (!isEnterpriseLogout) {
      document.location.href = withJamParams('/log-in', location.search)
    }
  }

  const fetchAsyncLocations = async (search, page) => {
    const response = await props.fetchLocations(search, page)
    return { ...response, locations: getSelectableLocations(response.locations) }
  }

  const handleClickLogOut = () => {
    props.dispatchSweetAlert({
      title: 'Are you sure you want to sign out?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, sign out',
      confirmBtnCssClass: 'mf-primary-btn alert-width-btn',
      cancelButtonText: 'No, cancel',
      cancelBtnCssClass: 'mf-outline-btn alert-width-btn',
      showLoaderOnConfirm: true,
      onConfirm: signOut
    })
  }

  const checkCredits = () => {
    setNoCredits(calculateNoCredits(props.userCredits))
  }

  const toggleDropdown = () => {
    setDropdownOpen(prevState => !prevState)
  }

  const toggleClientNameTooltip = () => {
    setClientNameTooltipOpen(prevState => !prevState)
  }

  const logInUrl = `/${portal.custom_login_url || 'log-in'}`
  const isMobile = windowWidth < 768
  const accountColSize = noCredits ? 12 : 12
  const creditColSize = noCredits ? 0 : 6

  if (!portal || props.sessionLoading) {
    return null
  }

  if (!isAdminUserPath && props.checkoutFlag.status === 'error') {
    return (
      <div role="alert">
        Could not load checkout options. <button onClick={props.checkoutFlag.retry}>Retry</button>
      </div>
    )
  }

  if (!isAdminUserPath && props.checkoutFlag.status === 'loading') {
    return <LoadingSpinner />
  }

  const currentTime = new Date()
  const headerColorOverride = getHeaderColorOverride({ portal, isAdminUserPath })
  const headerScopeClassName = `${isAdminUserPath ? 'admin-user-header' : 'storefront-header'}${
    headerColorOverride ? ' portal-header--custom-colors' : ''
  }`
  const headerColorStyle = headerColorOverride
    ? {
        '--mf-header-background-color': headerColorOverride.backgroundColor,
        '--mf-header-foreground-color': headerColorOverride.foregroundColor
      }
    : undefined

  return (
    <div className={headerScopeClassName} style={headerColorStyle}>
      {dropdownOpen && <div className="dropdown-backdrop" onClick={toggleDropdown}></div>}
      {!isAdminUserPath &&
      portal.show_announcement &&
      (!portal.announcement_starts_at || new Date(portal.announcement_starts_at) <= currentTime) &&
      (!portal.announcement_ends_at || new Date(portal.announcement_ends_at) >= currentTime) ? (
        <div
          className="mf-text-center"
          style={{
            color: portal.announcement_hex_color,
            backgroundColor: portal.announcement_hex_bg,
            padding: '15px'
          }}
          dangerouslySetInnerHTML={{ __html: portal.announcement_text }}
        />
      ) : null}

      <AppBar
        position="static"
        className={`iron-header-wrapper header-color iron-header-v1 ${fixedHeader ? 'header-fixed' : ''}`}
      >
        {portal.portal_has_top_menu === true && (
          <div className="top-links-container">
            <div className="top-links-area-mobile">
              <Row className="mobile-top-row">
                {isMobile && !isPunchoutMode ? (
                  <Col xs={0} className="mobile-credits">
                    {' '}
                    {/* TODO: add ErrorBoundary around lazy V2 slots — see feature-006 review */}
                    {isV2 ? (
                      <Suspense fallback={null}>
                        <CreditDisplay />
                      </Suspense>
                    ) : (
                      <CreditDisplayBox />
                    )}{' '}
                  </Col>
                ) : null}
                {isMobile && !currentUser.id ? (
                  <>
                    <Link to={logInUrl}>
                      <Button className="button mf-primary-nav-btn" style={{ width: '80px' }}>
                        {portal.portal_has_header_login_link && 'Sign In'}
                      </Button>
                    </Link>
                  </>
                ) : null}
                <Col xs={`${isMobile ? 0 : accountColSize}`} className="mobile-account">
                  {isAdminUserPath && (
                    <div style={{ marginRight: 'auto' }}>
                      <h2
                        id="client-name-tooltip"
                        onMouseEnter={toggleClientNameTooltip}
                        onMouseLeave={toggleClientNameTooltip}
                      >
                        {currentUser.client_name && currentUser.client_name.length > 50
                          ? `${currentUser.client_name.substring(0, 50)}...`
                          : currentUser.client_name}
                      </h2>
                      {currentUser.client_name && currentUser.client_name.length > 50 && (
                        <Tooltip
                          isOpen={clientNameTooltipOpen}
                          target="client-name-tooltip"
                          toggle={toggleClientNameTooltip}
                          placement="bottom"
                        >
                          {currentUser.client_name}
                        </Tooltip>
                      )}
                    </div>
                  )}
                  {!isMobile && !isAdminUserPath ? (
                    <div
                      className="first navbar-location"
                      style={{ minHeight: '50px', marginRight: '20px', maxWidth: '170px' }}
                    >
                      {currentUser && selectableLocations.length > 1 && canSwitchLocations && (
                        <SelectPaginate
                          id="location"
                          className={dropdownOpen ? 'dropdown-background' : ''}
                          onChange={changeLocation}
                          defaultValue={localStorage.getItem('currentlocationId') || props.currentLocation?.id}
                          value={props.currentLocation}
                          fetchData={fetchAsyncLocations}
                          data="locations"
                        />
                      )}
                    </div>
                  ) : null}

                  {currentUser.id ? (
                    <MyAccountDropdown
                      dropdownOpen={dropdownOpen}
                      toggleDropdown={toggleDropdown}
                      currentUser={currentUser}
                      props={props}
                      handleClickLogOut={handleClickLogOut}
                      changeLocation={changeLocation}
                      fetchAsyncLocations={fetchAsyncLocations}
                      isMobile={isMobile}
                      portal={portal}
                      isAdminUserPath={isAdminUserPath}
                      selectableLocations={selectableLocations}
                      canSwitchLocations={canSwitchLocations}
                      isPunchoutMode={isPunchoutMode}
                    />
                  ) : (
                    <ul id="headerlinks" className={`links ${showLinks ? 'show' : ''}`}>
                      <li className="first">
                        <span>
                          Welcome {currentUser.id ? <span data-jam-blur>{currentUser.first_name} </span> : ''}
                          {portal.id !== 31 ? `to ${portal.name}` : ''}
                        </span>
                      </li>
                      {currentUser.id && props.locations.length > 1 && !isAdminUserPath && (
                        <li className="first navbar-location">
                          <SelectPaginate
                            id="location"
                            onChange={changeLocation}
                            defaultValue={localStorage.getItem('currentlocationId') || props.currentLocation}
                            value={props.currentLocation}
                            fetchData={fetchAsyncLocations}
                            data="locations"
                          />
                        </li>
                      )}
                      <li className="first">
                        {props.sessionLoading ? (
                          <LoadingSpinner />
                        ) : (
                          <Link to={logInUrl}>
                            <Button className="button mf-primary-nav-btn" style={{ width: '80px' }}>
                              {portal.portal_has_header_login_link && 'Sign In'}
                            </Button>
                          </Link>
                        )}
                      </li>
                    </ul>
                  )}
                </Col>
              </Row>
            </div>
          </div>
        )}

        {!isAdminUserPath && (
          <>
            {isMobile ? (
              // Render for mobile view
              <React.Fragment>
                <Row className="px-sm mf-py-10 mx-lg">
                  {/* LOGO */}
                  <Col
                    xs={12}
                    className="d-flex justify-content-center align-items-center mf-px-0"
                    style={{ margin: '0' }}
                  >
                    <div className="iron-app-logo">
                      <a href={withJamParams('/', location.search)}>
                        {portal.logo?.url ? (
                          <img
                            src={portal.logo.url}
                            alt="UserImage"
                            key={portal.logo.url}
                            style={{ maxHeight: '100%' }}
                          />
                        ) : null}
                      </a>
                    </div>
                  </Col>
                </Row>
                <Row className="align-items-center search-credit-row" style={{ marginRight: 0 }}>
                  {/* SEARCH BAR */}
                  <Col xs={10} className="header-search-bar mf-px-0">
                    <SearchBar mobile={true} />
                  </Col>

                  {/* CREDIT BOXES & CART ICON */}
                  <Col xs={2} className="credit-boxes mf-px-0">
                    {isV2 ? (
                      <Suspense fallback={null}>
                        <HeaderCart />
                      </Suspense>
                    ) : (
                      <LegacyHeaderCart />
                    )}
                  </Col>
                </Row>
              </React.Fragment>
            ) : (
              <Row
                className="px-sm py-30 mx-lg non-mobile-logo-row"
                id="logo-search-credit-cart"
                style={{ justifyContent: 'space-evenly' }}
              >
                {/* LOGO */}
                <Col
                  md={3}
                  className="d-flex justify-content-center align-items-center mf-px-0"
                  style={{ margin: '0', paddingRight: '10px' }}
                >
                  <div className="iron-app-logo">
                    <a href={withJamParams('/', location.search)}>
                      {portal.logo?.url ? (
                        <img
                          src={portal.logo.url}
                          alt="UserImage"
                          key={portal.logo.url}
                          style={{ maxHeight: '100%' }}
                        />
                      ) : null}
                    </a>
                  </div>
                </Col>

                {/* SEARCH BAR */}
                <Col
                  md={6}
                  className="d-flex justify-content-center align-items-center header-search-bar mf-px-0"
                  style={{ width: '100%', margin: 0 }}
                >
                  <SearchBar mobile={false} />
                </Col>

                {/* CREDIT BOXES & CART ICON */}
                <Col
                  md={3}
                  className="d-flex justify-content-end align-items-center header-col-sm credit-boxes mf-px-0"
                  style={{ margin: 'auto 0' }}
                >
                  {!isPunchoutMode &&
                    (isV2 ? (
                      <Suspense fallback={null}>
                        <CreditDisplay />
                      </Suspense>
                    ) : (
                      <CreditDisplayBox mobile={false} />
                    ))}
                  &nbsp;&nbsp;
                  {isV2 ? (
                    <Suspense fallback={null}>
                      <HeaderCart />
                    </Suspense>
                  ) : (
                    <LegacyHeaderCart />
                  )}
                </Col>
              </Row>
            )}
          </>
        )}
        <div
          className="d-flex iron-header-bottom mf-text-center flex-align-center justify-content-middle"
          style={isAdminUserPath ? { minHeight: '56px' } : undefined}
        >
          {isAdminUserPath && <AdminHeaderActions currentPortalId={portal.id} userPortals={currentUser.user_portals} />}
          {!isAdminUserPath && customerGroup.id && currentLocation && currentLocation.hide_header_menu !== true && (
            <HeaderMenu location={currentLocation} hidden_categories={customerGroup.hidden_categories_list} />
          )}
        </div>
      </AppBar>
    </div>
  )
}

const mapStateToProps = state => {
  return {
    userCredits: state.userCredits,
    locations: state.storeViews.available,
    portal: state.portal,
    customerGroup: state.customerGroup,
    cartItemNumber: state.carts,
    currentLocation: state.storeViews.current,
    currentUser: state.currentUser,
    downloadableItems: state.myDownloads.items,
    socialPosts: state.mySocialPosts.items,
    sessionLoading: state.loading.session,
    session: state.session
  }
}

HeaderOne.propTypes = {
  checkoutFlag: PropTypes.shape({
    status: PropTypes.oneOf(['loading', 'error', 'ready']).isRequired,
    enabled: PropTypes.bool,
    retry: PropTypes.func
  }).isRequired
}

export default connect(mapStateToProps, {
  clearPaginatedProducts,
  fetchCurrentStoreView,
  fetchLocationProducts,
  fetchLocations,
  deleteSession,
  dispatchSweetAlert,
  setLoading
})(HeaderOne)
